"use client";

import { CheckCircleIcon } from '@heroicons/react/24/outline';
import TechnologyPartnerLogo, {
  type TechnologyPartnerLogoId,
} from './TechnologyPartnerLogo';

export default function TechnologyPartnershipsSection() {
  const technologies: {
    name: string;
    category: string;
    logoId: TechnologyPartnerLogoId;
  }[] = [
    { name: 'Snowflake', category: 'Cloud Data Warehouse', logoId: 'snowflake' },
    { name: 'AWS', category: 'Cloud Infrastructure', logoId: 'aws' },
    { name: 'Azure', category: 'Cloud Platform', logoId: 'azure' },
    { name: 'Google Cloud', category: 'Cloud Services', logoId: 'googlecloud' },
    { name: 'Databricks', category: 'Data Lakehouse', logoId: 'databricks' },
    { name: 'dbt', category: 'Data Transformation', logoId: 'dbt' },
    { name: 'Tableau', category: 'Business Intelligence', logoId: 'tableau' },
    { name: 'Power BI', category: 'Analytics Platform', logoId: 'powerbi' },
    { name: 'Fivetran', category: 'Data Integration', logoId: 'fivetran' },
    { name: 'Apache Airflow', category: 'Workflow Orchestration', logoId: 'airflow' },
    { name: 'Looker', category: 'Data Visualization', logoId: 'looker' },
    { name: 'Redshift', category: 'Data Warehouse', logoId: 'redshift' },
  ];

  const selectionCriteria = [
    'Reliable and proven in enterprise environments',
    'Scalable to handle growing data volumes',
    'Secure with enterprise-grade compliance',
    'Widely used by large enterprises globally',
    'Proven to deliver strong business results',
    'Backed by continuous innovation'
  ];

  return (
    <section className="bg-navy py-12 sm:py-16 md:py-24 px-4 sm:px-6 min-h-0">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            TECHNOLOGY PARTNERS
          </p>
          <h2 className="font-serif text-white text-[26px] sm:text-[36px] md:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            Technology Partnerships
          </h2>
          <p className="text-white/90 text-base sm:text-lg md:text-xl leading-[1.7] max-w-3xl md:mx-auto">
            We work with the industry&apos;s leading platforms to deliver best-in-class data solutions
          </p>
        </div>

        {/* Technology Logo Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 mb-10 sm:mb-16">
          {technologies?.map((tech, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl p-3 sm:p-5 hover:bg-white/15 hover:border-gold/50 transition-all duration-300 flex flex-row items-center gap-3 sm:gap-4 text-left min-w-0"
            >
              <TechnologyPartnerLogo
                logoId={tech.logoId}
                className="h-9 w-9 sm:h-11 sm:w-11 text-white shrink-0 opacity-95"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-white font-bold text-sm sm:text-base mb-0.5 leading-snug">
                  {tech?.name}
                </h3>
                <p className="text-white/60 text-[11px] sm:text-xs leading-snug">
                  {tech?.category}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Selection Criteria */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-10 mb-8 sm:mb-12">
          <h3 className="font-serif text-white text-xl sm:text-2xl font-bold text-left md:text-center mb-6 sm:mb-8">
            Why These Platforms?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl md:mx-auto">
            {selectionCriteria?.map((criterion, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0 mt-0.5" />
                <p className="text-white/90 text-[14px] sm:text-base leading-[1.7]">
                  {criterion}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-left md:text-center">
          <button className="min-h-[48px] w-full sm:w-auto bg-gold hover:bg-gold/90 text-navy px-6 sm:px-10 py-4 rounded-md text-sm font-semibold uppercase tracking-[1.5px] transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] duration-300">
            Explore Our Technology Stack
          </button>
          <p className="text-white/70 text-xs sm:text-sm mt-4 sm:mt-6 md:mx-auto max-w-md sm:max-w-none">
            Want to discuss which platform is right for your organization? Let&apos;s talk.
          </p>
        </div>
      </div>
    </section>
  );
}